// caiet UC60-01 / UC61-01, UC-DASH-01/03/04/07/11/12 — "Tablouri de bord", the reading surface.
//
// Owner decision D9 (docs/decizii-paritate-wireframe-2026-09-05.md): this screen IS the
// dashboard, not an index of links to it. The wireframe draws one bordered card filling the
// content width (wireframes/react/src/pages/Tablouri.tsx:47-80, `.grafana-embed` in
// wireframes/react/src/styles/mud.css:1893) whose top bar says where the picture comes from and
// how many dashboards this role reaches. The reader sees data at the first click, and the
// provenance line says why they are allowed to see it — which is the gate spec 001 built.
//
// The chooser lives inside that frame, and every entry is an address under
// /:role/dashboards/:slug. So the deep links handed out elsewhere — the administration screens'
// "Deschide" action, a saved bookmark — still resolve onto this same page (FR-025), and the
// browser's Back still moves between dashboards. Without a slug the frame opens on the first
// dashboard the role reaches.
//
// What stays on SIMSM's side of the boundary is unchanged. The list is SIMSM's own and not
// Grafana's browse page, for the two reasons the specification records: UC-DASH-04's interval
// limits are offered here (Grafana's own picker has none; the proxy refuses a query outside them,
// docs/shape-ops-dashboard-time-range.md §2.3), and Grafana's browsing surface is not rendered in
// the three interface languages ACCS-01 requires. The interval and the refresh period the viewer
// picks here last as long as the page and change nothing in the definition (UC-DASH-01/03/07).
// The titles and the embedded dashboard follow the interface language (ACCS-16, CF-18): the bff
// answers each definition with its Russian and English, and the page reads the one it speaks
// (docs/shape-ops-dashboard-localization.md §2.4). The frame itself goes
// through the authenticated proxy, which authorises the uid against
// analytics.dashboard_config.role_scope before Grafana sees the request; nothing here bypasses
// it. Authoring and role sharing remain next door on /system-admin/dashboards-admin.
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTolgee, useTranslate } from '@tolgee/react';
import { MudButton, MudIcon, MudInfoBox, MudSelect, MudTag } from '@egov-moldova/mud-react';
import {
  DASHBOARD_DEFAULT_RANGES,
  dashboardDateRangeRefusal,
  defaultRangeDays,
  earliestStartDate,
  effectiveBounds,
  localizeDashboard,
  rangeLimitDays,
  type DashboardRangeRefusal,
  type DashboardTranslation,
} from '@simsm/shared-types';
import { DEFAULT_LOCALE } from '@simsm/ui-i18n';
import {
  EmptyState,
  RouterLink,
  dateInputToIso,
  formatDate,
  isoToDateInput,
  DraftDateInput,
} from '@simsm/ui-shared';
import { apiClient, loadFailureKind } from '../../app/api-client';
import { useSession } from '../../hooks/use-session';
import { QueryState } from '../../components/QueryState/QueryState';
import { ObserverScopeBanner } from '../../components/AppShell/ObserverScopeBanner';
import { grafanaViewSrc, type FrameDates } from '../../features/dashboards/grafana-embed';
import './dashboards-page.css';

interface DashboardRow {
  slug: string;
  // Q-1-20: the System Administrator's list carries the inactive dashboards, marked by this flag;
  // absent from an older answer, which only ever listed active ones.
  active?: boolean;
  title: string;
  description: string;
  translations?: Partial<DashboardTranslation>[];
}

interface DashboardDetail extends DashboardRow {
  grafanaUid: string;
  refreshIntervalSeconds: number;
  defaultRange: string;
  maxRange: string;
  maxLookback: string;
}

// Q-1-20 (owner decision 2026-10-13): the System Administrator's list holds every dashboard, and
// this filter narrows it (Activ / Inactiv / Toate). The wire value is the BFF's `status` query.
export const DASHBOARD_STATUS_CHOICES = ['all', 'active', 'inactive'] as const;
export type DashboardStatusChoice = (typeof DASHBOARD_STATUS_CHOICES)[number];

// The heading line over the chooser says how many the reader sees, in the words of what is shown.
export function dashboardCountKey(
  isAdmin: boolean,
  status: DashboardStatusChoice,
  count: number,
): string {
  const form = count === 1 ? 'one' : 'many';
  if (!isAdmin) return `dashboards_page.embed.shared_${form}`;
  return `dashboards_page.embed.${status === 'all' ? 'total' : status}_${form}`;
}

// An address without a slug opens the first dashboard the role reaches, an active one before
// an inactive one: the inactive ones are listed to be found and reactivated, not to lead.
export function firstDashboardSlug(rows: readonly DashboardRow[]): string | undefined {
  return (rows.find((row) => row.active !== false) ?? rows[0])?.slug;
}

// The select's value for the viewer's own interval, beside the preset codes.
const CUSTOM_RANGE = 'custom';
// The select's value for "no refresh", beside the periods in seconds.
const REFRESH_OFF = 'off';
// The wireframe's list (IMPROSPATARI in wireframes/react/src/lib/admin-data.ts): 1 min, 5 min,
// 15 min, 1 hour and manual, which is Off here.
const REFRESH_CHOICES = [60, 300, 900, 3600];

// UC-DASH-04: the viewer changes the interval, but only inside the limits the
// definition sets, its length and how far back it starts. The dashboard's own
// default is always offered, even when it sits outside them, so a misconfigured
// definition still renders something rather than an empty select (the gate
// admits it too, docs/shape-ops-dashboard-time-range.md §2.2).
export function selectableRanges(
  defaultRange: string,
  maxRange: string,
  maxLookback = maxRange,
): string[] {
  const ceiling = Math.min(rangeLimitDays(maxRange), rangeLimitDays(maxLookback));
  const inside: string[] = DASHBOARD_DEFAULT_RANGES.filter(
    (range) => defaultRangeDays(range) <= ceiling,
  );
  return inside.includes(defaultRange) ? inside : [defaultRange, ...inside];
}

// UC-DASH-07: the viewer's periods, with the definition's own and the one in
// force among them, so the select always shows the current value.
export function refreshChoices(inForce: readonly (number | null)[]): number[] {
  const periods = new Set(REFRESH_CHOICES);
  for (const seconds of inForce) {
    if (seconds !== null && seconds > 0) periods.add(seconds);
  }
  return [...periods].sort((a, b) => a - b);
}

// What a failed read of one dashboard may honestly say.
//
// Only a 404 licenses the sentence about sharing. The bff answers 404 for both
// "no such slug" and "not shared with your role" on purpose — it will not tell a
// viewer which dashboards exist — so that one sentence covers both. Every other
// failure was reaching it too, which reported an outage to the viewer as a
// decision somebody had made about their permissions; and it offered no retry,
// because there is nothing to retry about a dashboard that was never shared.
export function dashboardErrorCopy(err: unknown): { key: string; retryable: boolean } {
  switch (loadFailureKind(err)) {
    case 'not_found':
      return { key: 'dashboard_view.not_found', retryable: false };
    case 'forbidden':
      return { key: 'common.forbidden', retryable: false };
    default:
      return { key: 'dashboard_view.load_error', retryable: true };
  }
}

export function DashboardsPage(): JSX.Element {
  const { t } = useTranslate();
  const locale = useTolgee(['language']).getLanguage() ?? DEFAULT_LOCALE;
  // The route tree is /:role/*, so the current segment is the prefix every chooser entry keeps:
  // a deep link opened as the System Administrator must not throw the reader into /observer.
  const { role: roleSegment, slug } = useParams<{ role: string; slug: string }>();
  const rolePrefix = `/${roleSegment ?? 'observer'}`;
  const session = useSession();
  const isAdmin = session.data?.activeRole === 'SYSTEM_ADMIN';
  // '' is the definition's own default interval and period.
  const [preset, setPreset] = useState('');
  const [custom, setCustom] = useState({ on: false, from: '', to: '' });
  const [refreshChoice, setRefreshChoice] = useState('');
  const [status, setStatus] = useState<DashboardStatusChoice>('all');

  // The same endpoint for every role: the BFF answers it with what this role may read
  // (dashboards.controller.ts), so the page never decides visibility for itself.
  const dashboards = useQuery({
    queryKey: ['dashboards-list', status],
    queryFn: () =>
      apiClient
        .get<{ dashboards: DashboardRow[] }>('/dashboards', {
          // Only the administrator's list is narrowed; the BFF ignores it for an observer.
          ...(status === 'all' ? {} : { params: { status } }),
        })
        .then((r) => r.data.dashboards ?? []),
    // A change of filter keeps the previous list on screen until the next one arrives, so the
    // page does not fall back to its loading line (and the filter does not blink) between two
    // choices.
    placeholderData: keepPreviousData,
  });
  const rows = (dashboards.data ?? []).map((row) => localizeDashboard(row, locale));

  // An address without a slug opens the first dashboard the role reaches. An address WITH one is
  // fetched as it stands, even when it is absent from the list above: only the bff may answer
  // whether that dashboard exists and is shared, and its 404 is the sentence the reader gets.
  // Falling back to the first row here would answer a refused deep link with somebody else's
  // dashboard.
  const current = slug ?? firstDashboardSlug(rows);

  const detail = useQuery({
    queryKey: ['dashboard-view', current],
    queryFn: () =>
      apiClient
        .get<DashboardDetail>(`/dashboards/${encodeURIComponent(current ?? '')}`)
        .then((r) => r.data),
    enabled: Boolean(current),
    refetchInterval: (query) => {
      const seconds = query.state.data?.refreshIntervalSeconds;
      return seconds && seconds > 0 ? seconds * 1000 : false;
    },
  });
  const data = detail.data ? localizeDashboard(detail.data, locale) : undefined;
  const maxLookback = data ? data.maxLookback || data.maxRange : '';
  const ranges = data ? selectableRanges(data.defaultRange, data.maxRange, maxLookback) : [];
  const activePreset = data ? (ranges.includes(preset) ? preset : data.defaultRange) : '';

  // The viewer's own interval moves the frame only once both dates are whole and
  // inside the limits; until then the frame stays on the preset and one line under
  // the dates says why.
  const nowMs = Date.now();
  const bounds = data
    ? effectiveBounds({ defaultRange: data.defaultRange, maxRange: data.maxRange, maxLookback })
    : null;
  const earliest = bounds ? earliestStartDate(bounds, nowMs) : null;
  const customRefusal: DashboardRangeRefusal | null =
    bounds && custom.on && custom.from && custom.to
      ? dashboardDateRangeRefusal(bounds, custom.from, custom.to, nowMs)
      : null;
  const customDates: FrameDates | null =
    custom.on && custom.from && custom.to && customRefusal === null
      ? { from: custom.from, to: custom.to }
      : null;
  const frameRange: string | FrameDates = customDates ?? activePreset;

  const configuredRefresh = data?.refreshIntervalSeconds ?? 0;
  const refreshSeconds: number | null =
    refreshChoice === REFRESH_OFF
      ? null
      : refreshChoice
        ? Number(refreshChoice)
        : configuredRefresh;

  const lengthLabel = data ? t(`dashboards_admin.max_range.${data.maxRange}`) : '';
  const customHint = !bounds
    ? null
    : Number.isFinite(rangeLimitDays(data?.maxRange ?? ''))
      ? earliest
        ? t('dashboard_view.range.custom_hint', { limit: lengthLabel, date: formatDate(earliest) })
        : t('dashboard_view.range.custom_hint_length', { limit: lengthLabel })
      : earliest
        ? t('dashboard_view.range.custom_hint_start', { date: formatDate(earliest) })
        : null;
  const refusalCopy: Record<DashboardRangeRefusal, string> = {
    order: t('dashboard_view.range.error.order'),
    length: t('dashboard_view.range.error.length', { limit: lengthLabel }),
    lookback: t('dashboard_view.range.error.lookback', { date: formatDate(earliest ?? '') }),
  };

  function refreshLabel(seconds: number): string {
    if (seconds % 3600 === 0) return t('dashboard_view.refresh.hours', { count: seconds / 3600 });
    if (seconds % 60 === 0) return t('dashboard_view.refresh.minutes', { count: seconds / 60 });
    return t('dashboard_view.refresh.seconds', { count: seconds });
  }

  const countKey = dashboardCountKey(isAdmin, status, rows.length);

  return (
    <section className="simsm-dashboards-page">
      <header className="simsm-dashboards-page__header">
        {/* FR-023: the heading reads the menu entry's own words. */}
        <h1 className="simsm-dashboards-page__title">{t('nav.dashboards')}</h1>
        {session.data?.activeRole === undefined ? null : (
          <p className="simsm-dashboards-page__lede">
            {isAdmin ? t('dashboards_page.lede_admin') : t('dashboards_page.lede_observer')}
          </p>
        )}
        {isAdmin ? (
          <RouterLink
            className="simsm-dashboards-page__manage"
            to="/system-admin/dashboards-admin"
            size="sm"
          >
            {t('dashboards_page.manage_link')}
          </RouterLink>
        ) : null}
      </header>

      <ObserverScopeBanner context="dashboards" />

      {/* Q-1-20: the filter stays outside the query's states. An empty answer to «Inactiv» (the
          usual case) is exactly when the empty message tells the administrator to pick another
          status, so the control must still be on screen then. */}
      {isAdmin ? (
        <div className="simsm-dashboards-page__filter">
          <StatusFilter value={status} onChange={setStatus} />
        </div>
      ) : null}

      <QueryState
        query={dashboards}
        isEmpty={rows.length === 0}
        empty={
          /* The wireframe draws this screen's empty at Tablouri.tsx:37: a `.card`
             around the `.empty`, the dashboard glyph, "Niciun tablou partajat cu
             rolul tău", and the line saying what makes one appear. AGE ships no
             tile-grid glyph, so it is `chart` — already this page's own mark for
             a dashboard, on the provenance bar below.
             The wireframe has only the observer's screen. The admin/observer
             split is ours and it stays: to an administrator the same absence has
             a different cause and a different next step, and that sentence is
             the only thing on a blank screen that says where to go. */
          <EmptyState
            icon="chart"
            title={
              isAdmin
                ? status === 'all'
                  ? t('dashboards_page.empty.title_admin')
                  : t('dashboards_page.empty.title_admin_filtered')
                : t('dashboards_page.empty.title_observer')
            }
            body={
              isAdmin
                ? status === 'all'
                  ? t('dashboards_page.empty.body_admin')
                  : t('dashboards_page.empty.body_admin_filtered')
                : t('dashboards_page.empty.body_observer')
            }
          />
        }
      >
        <div className="simsm-dashboards-page__embed">
          <div className="simsm-dashboards-page__bar">
            <MudIcon name="chart" size={16} />
            <span className="simsm-dashboards-page__bar-text">
              <strong>{t('dashboard_view.widget_kind.grafana')}</strong> ·{' '}
              {t('dashboards_page.embed.provenance')}
            </span>
            <span className="simsm-dashboards-page__bar-count">
              {t(countKey, { count: rows.length })}
            </span>
          </div>

          <div className="simsm-dashboards-page__controls">
            {/* The chooser: one address per dashboard, so the frame and the address bar say the
                same thing and a deep link is what a click produces. */}
            <nav
              aria-label={t('dashboard_view.list.aria_label')}
              className="simsm-dashboards-page__list"
            >
              {rows.map((row) => (
                <span key={row.slug} className="simsm-dashboards-page__choice-item">
                  <RouterLink
                    className={
                      row.slug === current
                        ? 'simsm-dashboards-page__choice simsm-dashboards-page__choice--current'
                        : 'simsm-dashboards-page__choice'
                    }
                    to={`${rolePrefix}/dashboards/${row.slug}`}
                    size="sm"
                    variant={row.slug === current ? 'primary' : 'strict'}
                    underline="hover"
                    aria-current={row.slug === current ? 'page' : undefined}
                  >
                    {row.title}
                  </RouterLink>
                  {row.active === false ? (
                    <MudTag
                      variant="status"
                      semantic="neutral"
                      label={t('dashboards_admin.status.inactive')}
                    />
                  ) : null}
                </span>
              ))}
            </nav>

            {data ? (
              <div className="simsm-dashboards-page__view-controls">
                <MudSelect
                  className="simsm-dashboards-page__range"
                  size="medium"
                  value={custom.on ? CUSTOM_RANGE : activePreset}
                  label={t('dashboard_view.range_label')}
                  onMudChange={(e: CustomEvent<{ value: string }>) => {
                    if (e.detail.value === CUSTOM_RANGE) {
                      setCustom((current) => ({ ...current, on: true }));
                    } else {
                      setPreset(e.detail.value);
                      setCustom((current) => ({ ...current, on: false }));
                    }
                  }}
                >
                  {ranges.map((value) => (
                    <option key={value} value={value}>
                      {t(`dashboards_admin.default_range.${value}`)}
                    </option>
                  ))}
                  <option value={CUSTOM_RANGE}>{t('dashboard_view.range.custom')}</option>
                </MudSelect>
                {custom.on ? (
                  <>
                    {/* `min`/`max` stay ISO: AGE compares bounds on the wire value. */}
                    <DraftDateInput
                      className="simsm-dashboards-page__date"
                      placeholder={t('common.date.placeholder')}
                      size="md"
                      label={t('dashboard_view.range.from')}
                      value={isoToDateInput(custom.from)}
                      invalid={customRefusal !== null}
                      {...(earliest ? { min: earliest } : {})}
                      {...(custom.to ? { max: custom.to } : {})}
                      onMudChange={(e: CustomEvent<{ value: string }>) => {
                        const from = dateInputToIso(e.detail.value);
                        setCustom((current) => ({ ...current, from }));
                      }}
                    />
                    <DraftDateInput
                      className="simsm-dashboards-page__date"
                      placeholder={t('common.date.placeholder')}
                      size="md"
                      label={t('dashboard_view.range.to')}
                      value={isoToDateInput(custom.to)}
                      invalid={customRefusal !== null}
                      {...(custom.from ? { min: custom.from } : {})}
                      onMudChange={(e: CustomEvent<{ value: string }>) => {
                        const to = dateInputToIso(e.detail.value);
                        setCustom((current) => ({ ...current, to }));
                      }}
                    />
                  </>
                ) : null}
                <MudSelect
                  className="simsm-dashboards-page__refresh"
                  size="medium"
                  value={refreshSeconds === null ? REFRESH_OFF : String(refreshSeconds)}
                  label={t('dashboard_view.refresh_label')}
                  onMudChange={(e: CustomEvent<{ value: string }>) =>
                    setRefreshChoice(e.detail.value)
                  }
                >
                  {refreshChoices([configuredRefresh, refreshSeconds]).map((seconds) => (
                    <option key={seconds} value={String(seconds)}>
                      {refreshLabel(seconds)}
                    </option>
                  ))}
                  <option value={REFRESH_OFF}>{t('dashboard_view.refresh.off')}</option>
                </MudSelect>
                {custom.on && (customRefusal !== null || customHint !== null) ? (
                  <p
                    className={
                      customRefusal !== null
                        ? 'simsm-dashboards-page__range-note simsm-dashboards-page__range-note--error'
                        : 'simsm-dashboards-page__range-note'
                    }
                    role="status"
                  >
                    {customRefusal !== null ? refusalCopy[customRefusal] : customHint}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          {isAdmin && data?.active === false ? (
            <MudInfoBox emphasis="strong" variant="info">
              {t('dashboards_page.inactive_notice')}{' '}
              <RouterLink to="/system-admin/dashboards-admin" size="sm">
                {t('dashboards_page.inactive_notice_action')}
              </RouterLink>
            </MudInfoBox>
          ) : null}

          <div className="simsm-dashboards-page__body">
            <DashboardFrame
              detail={detail}
              data={data}
              range={frameRange}
              refreshSeconds={refreshSeconds}
              onRetry={() => void detail.refetch()}
            />
          </div>
        </div>
      </QueryState>
    </section>
  );
}

// The administrator's status filter (Q-1-20): «Toate» first, since the list holds every dashboard.
function StatusFilter({
  value,
  onChange,
}: {
  value: DashboardStatusChoice;
  onChange: (next: DashboardStatusChoice) => void;
}): JSX.Element {
  const { t } = useTranslate();
  const label: Record<DashboardStatusChoice, string> = {
    all: t('dashboards_admin.filter.status_any'),
    active: t('dashboards_admin.status.active'),
    inactive: t('dashboards_admin.status.inactive'),
  };
  return (
    <MudSelect
      className="simsm-dashboards-page__status"
      size="medium"
      label={t('dashboards_admin.filter.status')}
      value={value}
      onMudChange={(e: CustomEvent<{ value: string }>) => {
        const next = e.detail.value;
        if ((DASHBOARD_STATUS_CHOICES as readonly string[]).includes(next)) {
          onChange(next as DashboardStatusChoice);
        }
      }}
    >
      {DASHBOARD_STATUS_CHOICES.map((choice) => (
        <option key={choice} value={choice}>
          {label[choice]}
        </option>
      ))}
    </MudSelect>
  );
}

function DashboardFrame({
  detail,
  data,
  range,
  refreshSeconds,
  onRetry,
}: {
  detail: { isError: boolean; error: unknown };
  data: DashboardDetail | undefined;
  range: string | FrameDates;
  refreshSeconds: number | null;
  onRetry: () => void;
}): JSX.Element {
  const { t } = useTranslate();

  if (detail.isError) {
    const { key, retryable } = dashboardErrorCopy(detail.error);
    return (
      <MudInfoBox emphasis="strong" role="alert" variant={retryable ? 'error' : 'warning'}>
        <span className="simsm-dashboards-page__error">
          <span>{t(key)}</span>
          {retryable ? (
            <MudButton shape="circular" variant="neutral" size="sm" onClick={onRetry}>
              {t('common.retry')}
            </MudButton>
          ) : null}
        </span>
      </MudInfoBox>
    );
  }
  if (!data) {
    return <p className="simsm-dashboards-page__loading">{t('dashboard_view.loading')}</p>;
  }
  if (!data.grafanaUid) {
    return (
      <MudInfoBox emphasis="strong" variant="info">
        {t('dashboard_view.grafana.no_uid')}
      </MudInfoBox>
    );
  }
  // A new address per interval and period: Grafana reads both from it, and every panel of
  // the dashboard reloads together (UC-DASH-07).
  const src = grafanaViewSrc(data.grafanaUid, range, refreshSeconds, data.slug);
  return (
    <iframe
      key={src}
      title={data.title}
      src={src}
      className="simsm-dashboards-page__frame"
      loading="lazy"
    />
  );
}
